import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { CorrelationComparison } from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

interface CorrelationComparisonTableProps {
  comparison: CorrelationComparison[];
  symbolNames: { [symbol: string]: string };
  asOfDate: string;
}

const getChangeColor = (change: number): string => {
  if (Math.abs(change) < 0.05) return bloomberg.text.secondary;
  return change > 0 ? '#4caf50' : '#f44336';
};

const getChangeIcon = (change: number) => {
  if (Math.abs(change) < 0.05) {
    return <TrendingFlatIcon fontSize="small" sx={{ color: bloomberg.text.secondary }} />;
  }
  return change > 0 ? (
    <TrendingUpIcon fontSize="small" sx={{ color: '#4caf50' }} />
  ) : (
    <TrendingDownIcon fontSize="small" sx={{ color: '#f44336' }} />
  );
};

const formatCorrelation = (value: number): string => {
  return value.toFixed(2);
};

const getChangeLabel = (change: number): string => {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${(change * 100).toFixed(0)}%`;
};

const CorrelationComparisonTable: React.FC<CorrelationComparisonTableProps> = ({
  comparison,
  symbolNames,
  asOfDate,
}) => {
  if (!comparison || comparison.length === 0) {
    return null;
  }

  // Only show top 15 changes
  const topChanges = comparison.slice(0, 15);

  return (
    <Paper sx={{ p: 2, mt: 3 }} elevation={1}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Correlation Changes
        <Chip label={`Since ${asOfDate}`} size="small" variant="outlined" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Comparing historical correlations to current values (sorted by largest change)
      </Typography>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Pair</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                Historical
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                Current
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>
                Change
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topChanges.map((item, index) => (
              <TableRow
                key={`${item.symbol1}-${item.symbol2}`}
                sx={{
                  bgcolor: index % 2 === 0 ? 'transparent' : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.symbol1} / {item.symbol2}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {symbolNames[item.symbol1]?.split(' ').slice(0, 2).join(' ')} vs{' '}
                      {symbolNames[item.symbol2]?.split(' ').slice(0, 2).join(' ')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      color: item.historical >= 0 ? '#4caf50' : '#f44336',
                      fontFamily: '"IBM Plex Mono", monospace',
                    }}
                  >
                    {formatCorrelation(item.historical)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      color: item.current >= 0 ? '#4caf50' : '#f44336',
                      fontFamily: '"IBM Plex Mono", monospace',
                    }}
                  >
                    {formatCorrelation(item.current)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    {getChangeIcon(item.change)}
                    <Typography
                      variant="body2"
                      sx={{
                        color: getChangeColor(item.change),
                        fontWeight: Math.abs(item.change) >= 0.1 ? 600 : 400,
                        fontFamily: '"IBM Plex Mono", monospace',
                      }}
                    >
                      {item.change >= 0 ? '+' : ''}
                      {formatCorrelation(item.change)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {comparison.length > 15 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing top 15 of {comparison.length} pairs
        </Typography>
      )}

      {/* Summary Statistics */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={`Increased: ${comparison.filter((c) => c.change > 0.05).length}`}
          sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
        />
        <Chip
          size="small"
          label={`Decreased: ${comparison.filter((c) => c.change < -0.05).length}`}
          sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', color: '#f44336' }}
        />
        <Chip
          size="small"
          label={`Stable: ${comparison.filter((c) => Math.abs(c.change) <= 0.05).length}`}
          sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', color: bloomberg.text.secondary }}
        />
      </Box>
    </Paper>
  );
};

export default CorrelationComparisonTable;
